import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    CardFooter,
} from "@component/card";
import {
    CheckCircleIcon,
    EyeIcon,
    EyeOffIcon,
} from "lucide-react";
import { authResetPassword } from "@service/auth";
import { catchErrorLog } from "@util/sentry";
import { sanitizeRedirectPath } from "@util/auth-redirect";

interface Props {
    token: string
}

export default function ResetPasswordForm(props: Props) {
  // `?next=` (the Setup email, #4414): where the login sends them once the password
  // is set. Same-origin paths only; anything else signs in as before.
  const [searchParams] = useSearchParams();
  const next = sanitizeRedirectPath(searchParams.get("next"), "");
  const loginPath = next ? `/?_redirect=${encodeURIComponent(next)}` : "/";
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showError, setShowError] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const validateFields = () => {
        // Check if password is empty
        if (!password) {
            setError("Password is required.");
            return false;
        }
        // Check if password is too short
        else if (password.length < 8) {
            setError("Password must be at least 8 characters long.");
            return false;
        }
        // Check if confirm password is empty
        else if (!confirmPassword) {
            setError("Please confirm your password.");
            return false;
        }
        // Check if passwords match
        else if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return false;
        }

        // Clear error if all validations pass
        setError(null);
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setShowError(true);

        if (!validateFields()) {
            return;
        }

        setIsLoading(true);

        authResetPassword({
            token: props.token,
            new_password: password,
            confirm_password: confirmPassword,
        }).then(() => {
            setIsSuccess(true);
        }).catch((error) => {
            catchErrorLog(`Failed to call reset password, err: ${error}`)
            setError(error.response.data.message || "Failed to reset password. Please try again.")
        }).finally(() => {
            setIsLoading(false);
        })
    };

    if (isSuccess) {
        return (
            <Card className="w-full max-w-md">
                <CardHeader className="space-y-1 text-center">
                    <div className="flex justify-center">
                        <CheckCircleIcon className="h-12 w-12 text-green-500" />
                    </div>
                    <CardTitle className="text-lg md:text-2xl font-bold">
                        Password Reset Successful
                    </CardTitle>
                    <CardDescription>
                        Your password has been successfully reset.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        You can now use your new password to log in to your account.
                    </p>
                </CardContent>
                <CardFooter className="flex justify-center">
                    <Link to={loginPath}>
                        <Button variant="default">
                            {next ? "Sign in and continue" : "Return to Login"}
                        </Button>
                    </Link>
                </CardFooter>
            </Card>
        );
    }

    return (
        <Card className="w-full max-w-md">
            <CardHeader className="space-y-1 text-center">
                <CardTitle className="text-lg md:text-2xl font-bold">
                    Reset Your Password
                </CardTitle>
                <CardDescription>
                    Create a new password for your account.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="password">New Password</Label>
                        <div className="relative">
                            <Input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className={
                                    showError && (!password || password.length < 8)
                                        ? "border-red-500 pr-10"
                                        : "pr-10"
                                }
                                required
                            />
                            <button
                                type="button"
                                className="absolute right-0 top-0 h-full px-3 py-2 text-gray-400 hover:text-gray-600"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? (
                                    <EyeOffIcon className="h-4 w-4" />
                                ) : (
                                    <EyeIcon className="h-4 w-4" />
                                )}
                                <span className="sr-only">
                                    {showPassword ? "Hide password" : "Show password"}
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="confirm-password">Confirm New Password</Label>
                        <div className="relative">
                            <Input
                                id="confirm-password"
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="••••••••"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className={
                                    showError &&
                                        (!confirmPassword || password !== confirmPassword)
                                        ? "border-red-500 pr-10"
                                        : "pr-10"
                                }
                                required
                            />

                            <button
                                type="button"
                                className="absolute right-0 top-0 h-full px-3 py-2 text-gray-400 hover:text-gray-600"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                                {showConfirmPassword ? (
                                    <EyeOffIcon className="h-4 w-4" />
                                ) : (
                                    <EyeIcon className="h-4 w-4" />
                                )}
                                <span className="sr-only">
                                    {showConfirmPassword ? "Hide password" : "Show password"}
                                </span>
                            </button>
                        </div>
                    </div>

                    {error && (
                        <p className="text-sm text-red-400">{error}</p>
                    )}

                    <Button
                        type="submit"
                        variant="default"
                        className="w-full"
                        disabled={isLoading}
                    >
                        {isLoading ? "Resetting..." : "Reset Password"}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
}
